<template>
  <!-- Customer Aftercare: Installed Systems, Device Purchases and Repairs.
       Front desk tags the category at invoice time; records land in their
       category's Pending Setup table ("No cadence set") until the CRM enters
       the cadence + turn-over/delivery date + mode, which starts the schedule.
       (Installed_Systems_and_Maintenance_Scheduling spec, OM Part 16.) -->
  <v-container fluid>
    <div class="d-flex align-center mb-3">
      <v-icon class="mr-2" color="primary">mdi-face-agent</v-icon>
      <h2>Customer Aftercare</h2>
      <v-spacer />
      <v-btn color="primary" @click="openCreate">
        <v-icon class="mr-1">mdi-plus</v-icon>New Record
      </v-btn>
    </div>

    <v-tabs v-model="tab" class="mb-3">
      <v-tab>
        Pending Setup
        <v-chip v-if="pendingSystems.length" x-small color="warning" class="ml-2">{{
          pendingSystems.length
        }}</v-chip>
      </v-tab>
      <v-tab>Register</v-tab>
      <v-tab>
        Maintenance Due
        <v-chip v-if="due_visits.length" x-small color="warning" class="ml-2">{{
          due_visits.length
        }}</v-chip>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- ============ PENDING SETUP ============ -->
      <v-tab-item>
        <v-card outlined>
          <v-card-text>
            <div class="d-flex align-center mb-2">
              <p class="caption grey--text mb-0">
                Newly invoiced records awaiting the CRM to set the cadence,
                turn-over/delivery date and mode. Setting these starts the
                schedule.
              </p>
              <v-spacer />
              <v-btn text color="success" @click="refresh" :loading="loading">
                <v-icon class="mr-1">mdi-refresh</v-icon>Refresh
              </v-btn>
            </div>

            <div v-if="!pendingSystems.length" class="text-center grey--text py-6">
              Nothing pending setup. 🎉
            </div>

            <div
              v-for="cat in categories"
              :key="cat.value"
              v-show="pendingByCategory(cat.value).length"
              class="mb-4"
            >
              <div class="section-label">
                <v-icon x-small>{{ cat.icon }}</v-icon>
                {{ cat.label }} ({{ pendingByCategory(cat.value).length }})
              </div>
              <v-simple-table dense class="bordered-table rounded-lg">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th style="width: 120px">Type</th>
                    <th>Site / Notes</th>
                    <th style="width: 110px">Invoiced</th>
                    <th style="width: 130px">Status</th>
                    <th style="width: 200px">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in pendingByCategory(cat.value)" :key="item.id">
                    <td>
                      {{ (item.customer && item.customer.customer_name) || "—" }}
                      <v-chip
                        x-small
                        class="ml-1"
                        :color="isGovernment(item) ? 'orange' : 'blue'"
                        dark
                      >
                        {{ isGovernment(item) ? "Gov" : "Walk-in" }}
                      </v-chip>
                    </td>
                    <td>{{ item.system_type }}</td>
                    <td class="text-caption">{{ item.site_address || "—" }}</td>
                    <td>{{ dateOnly(item.installed_on) }}</td>
                    <td>
                      <v-chip x-small color="orange" dark>
                        <v-icon left x-small>mdi-calendar-alert</v-icon>
                        No cadence set
                      </v-chip>
                    </td>
                    <td>
                      <v-btn
                        x-small
                        color="primary"
                        depressed
                        class="mr-1"
                        @click="openSetup(item)"
                      >
                        <v-icon left x-small>mdi-calendar-plus</v-icon>Set Up
                      </v-btn>
                      <v-btn icon small @click="openDetail(item)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                      <v-btn icon small color="error" @click="confirmDelete(item)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- ============ REGISTER ============ -->
      <v-tab-item>
        <v-card outlined>
          <v-card-text>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="3">
                <v-select
                  v-model="filters.category"
                  :items="categoryFilterItems"
                  label="Category"
                  dense
                  outlined
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="filters.branch_id"
                  :items="branchFilterItems"
                  label="Branch"
                  dense
                  outlined
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="filters.site_municipality"
                  :items="municipalityItems"
                  label="Municipality"
                  dense
                  outlined
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="filters.status"
                  :items="statusItems"
                  label="Status"
                  dense
                  outlined
                  hide-details
                  clearable
                />
              </v-col>
            </v-row>

            <v-data-table
              dense
              class="bordered-table rounded-lg"
              :loading="loading"
              :items="mappedSystems"
              :headers="headers"
              :search="search"
            >
              <template #top>
                <div class="d-flex align-center px-2 pb-2">
                  <v-text-field
                    v-model="search"
                    label="Search"
                    prepend-inner-icon="mdi-magnify"
                    dense
                    outlined
                    hide-details
                    style="max-width: 320px"
                  />
                  <v-spacer />
                  <v-btn text color="success" @click="refresh" :loading="loading">
                    <v-icon class="mr-1">mdi-refresh</v-icon>Refresh
                  </v-btn>
                </div>
              </template>
              <template #[`item.customer_name`]="{ item }">
                {{ (item.customer && item.customer.customer_name) || "—" }}
              </template>
              <template #[`item.category_label`]="{ item }">
                <v-chip x-small outlined :color="categoryColor(item.category)">
                  {{ categoryLabel(item.category) }}
                </v-chip>
                <v-chip
                  x-small
                  class="ml-1"
                  :color="isGovernment(item) ? 'orange' : 'blue'"
                  dark
                >
                  {{ isGovernment(item) ? "Gov" : "Walk-in" }}
                </v-chip>
              </template>
              <template #[`item.customer_contact`]="{ item }">
                <div
                  v-if="item.customer && item.customer.customer_contact_no"
                  class="d-flex align-center"
                >
                  <span>{{ item.customer.customer_contact_no }}</span>
                  <v-btn
                    icon
                    x-small
                    class="ml-1"
                    title="Copy number"
                    @click="copyText(item.customer.customer_contact_no)"
                  >
                    <v-icon x-small>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <span v-else class="grey--text">—</span>
                <div
                  v-if="item.end_user_contact"
                  class="d-flex align-center"
                >
                  <span class="caption">End user: {{ item.end_user_contact }}</span>
                  <v-btn
                    icon
                    x-small
                    class="ml-1"
                    title="Copy end-user number"
                    @click="copyText(item.end_user_contact)"
                  >
                    <v-icon x-small>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </template>
              <template #[`item.installed_on`]="{ item }">
                {{ dateOnly(item.installed_on) }}
              </template>
              <template #[`item.next_visit`]="{ item }">
                <v-chip v-if="item.needs_setup" x-small color="orange" dark>
                  No cadence set
                </v-chip>
                <template v-else-if="item.next_visit">
                  {{ dateOnly(item.next_visit.due_date) }}
                  <v-chip
                    x-small
                    outlined
                    :color="item.next_visit.visit_type === 'onsite' ? 'deep-purple' : 'blue'"
                    class="ml-1"
                  >
                    {{ item.next_visit.visit_type === "onsite" ? "onsite" : "call" }}
                    #{{ item.next_visit.visit_number }}
                  </v-chip>
                </template>
                <span v-else class="grey--text">—</span>
              </template>
              <template #[`item.status`]="{ item }">
                <v-chip x-small :color="statusColor(item.status)" dark>{{
                  item.status
                }}</v-chip>
              </template>
              <template #[`item.action`]="{ item }">
                <v-btn
                  v-if="item.needs_setup"
                  x-small
                  color="primary"
                  depressed
                  class="mr-1"
                  @click="openSetup(item)"
                >
                  Set Up
                </v-btn>
                <v-btn icon small @click="openDetail(item)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
                <v-btn icon small @click="openEdit(item)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon small color="error" @click="confirmDelete(item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- ============ MAINTENANCE DUE ============ -->
      <v-tab-item>
        <v-card outlined>
          <v-card-text>
            <v-row dense class="mb-2" align="center">
              <v-col cols="12" sm="3">
                <v-select
                  v-model="dueDays"
                  :items="dueDaysItems"
                  label="Due within"
                  dense
                  outlined
                  hide-details
                  @change="refreshDue"
                />
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="dueMunicipality"
                  :items="dueMunicipalityItems"
                  label="Municipality"
                  dense
                  outlined
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" sm="6" class="text-right">
                <v-btn text color="success" @click="refreshDue" :loading="due_loading">
                  <v-icon class="mr-1">mdi-refresh</v-icon>Refresh
                </v-btn>
              </v-col>
            </v-row>

            <p class="caption grey--text">
              On-site visits are batched by municipality — group these onto one
              trip. Call (remote) checks also appear here but are not calendar jobs.
            </p>

            <div v-if="!dueGroups.length" class="text-center grey--text py-6">
              No maintenance due in this window.
            </div>

            <div v-for="group in dueGroups" :key="group.municipality" class="mb-4">
              <div class="section-label">
                <v-icon x-small>mdi-map-marker</v-icon>
                {{ group.municipality }} ({{ group.visits.length }})
              </div>
              <v-simple-table dense class="bordered-table rounded-lg">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th style="width: 150px">Contact No.</th>
                    <th style="width: 120px">Category</th>
                    <th>System</th>
                    <th>Site</th>
                    <th style="width: 90px">Visit</th>
                    <th style="width: 100px">Type</th>
                    <th style="width: 120px">Due</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="v in group.visits"
                    :key="v.id"
                    style="cursor: pointer"
                    @click="openDetailById(v.installed_system_id)"
                  >
                    <td>
                      {{
                        (v.installed_system &&
                          v.installed_system.customer &&
                          v.installed_system.customer.customer_name) ||
                        "—"
                      }}
                    </td>
                    <td @click.stop>
                      <div v-if="contactOf(v)" class="d-flex align-center">
                        <span>{{ contactOf(v) }}</span>
                        <v-btn
                          icon
                          x-small
                          class="ml-1"
                          title="Copy number"
                          @click="copyText(contactOf(v))"
                        >
                          <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                      </div>
                      <span v-else class="grey--text">—</span>
                    </td>
                    <td>
                      <v-chip
                        x-small
                        outlined
                        :color="categoryColor(v.installed_system && v.installed_system.category)"
                      >
                        {{ categoryLabel(v.installed_system && v.installed_system.category) }}
                      </v-chip>
                    </td>
                    <td>{{ v.installed_system && v.installed_system.system_type }}</td>
                    <td class="text-caption">
                      {{ v.installed_system && v.installed_system.site_address }}
                    </td>
                    <td>
                      {{ v.visit_number }} of
                      {{ (v.installed_system && v.installed_system.max_visits) || 4 }}
                    </td>
                    <td>
                      <v-chip
                        x-small
                        outlined
                        :color="v.visit_type === 'onsite' ? 'deep-purple' : 'blue'"
                      >
                        {{ v.visit_type === "onsite" ? "onsite" : "call" }}
                      </v-chip>
                    </td>
                    <td>{{ dateOnly(v.due_date) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <installed-system-form-modal
      v-model="formDialog"
      :editing="editing"
      @saved="onSaved"
    />
    <installed-system-detail-modal
      v-model="detailDialog"
      :system-id="detailId"
      @changed="onChanged"
    />
    <aftercare-setup-modal
      v-model="setupDialog"
      :system="setupSystem"
      @saved="onSetupSaved"
    />
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import InstalledSystemFormModal from "../components/InstalledSystem/installedSystemFormModal.vue";
import InstalledSystemDetailModal from "../components/InstalledSystem/installedSystemDetailModal.vue";
import AftercareSetupModal from "../components/InstalledSystem/aftercareSetupModal.vue";

export default {
  name: "CustomerAftercare",
  components: {
    InstalledSystemFormModal,
    InstalledSystemDetailModal,
    AftercareSetupModal,
  },
  data() {
    return {
      tab: 0,
      search: "",
      filters: {
        category: null,
        branch_id: null,
        site_municipality: null,
        status: null,
      },
      dueDays: 30,
      dueMunicipality: null,
      dueDaysItems: [
        { text: "Next 30 days", value: 30 },
        { text: "Next 60 days", value: 60 },
        { text: "Next 90 days", value: 90 },
        { text: "Next 180 days", value: 180 },
      ],
      categories: [
        { value: "installed_system", label: "Installed Systems", icon: "mdi-cctv" },
        { value: "device_purchase", label: "Device Purchases", icon: "mdi-laptop" },
        { value: "repair", label: "Repairs", icon: "mdi-wrench" },
      ],
      statusItems: [
        { text: "Active", value: "active" },
        { text: "Dormant", value: "dormant" },
        { text: "Ended", value: "ended" },
      ],
      headers: [
        { text: "Customer", value: "customer_name" },
        { text: "Category", value: "category_label" },
        { text: "Contact No.", value: "customer_contact", sortable: false },
        { text: "Type", value: "system_type" },
        { text: "Site", value: "site_address" },
        { text: "Municipality", value: "site_municipality" },
        { text: "Date", value: "installed_on" },
        { text: "Next Follow-up", value: "next_visit", sortable: false },
        { text: "Status", value: "status" },
        { text: "Actions", value: "action", sortable: false },
      ],
      formDialog: false,
      detailDialog: false,
      setupDialog: false,
      editing: null,
      detailId: null,
      setupSystem: null,
    };
  },
  computed: {
    ...mapGetters({
      systems: "installed_system/systems",
      loading: "installed_system/loading",
      due_visits: "installed_system/due_visits",
      due_loading: "installed_system/due_loading",
      branches: "branch/branches",
      user: "auth/user",
    }),
    branchFilterItems() {
      return this.branches.map((b) => ({ text: b.branch_name, value: b.id }));
    },
    categoryFilterItems() {
      return this.categories.map((c) => ({ text: c.label, value: c.value }));
    },
    municipalityItems() {
      const set = new Set(
        this.systems.map((s) => s.site_municipality).filter(Boolean)
      );
      return Array.from(set).sort();
    },
    pendingSystems() {
      return this.systems.filter((s) => s.needs_setup);
    },
    filteredSystems() {
      return this.systems.filter((s) => {
        if (this.filters.category && s.category != this.filters.category)
          return false;
        if (this.filters.branch_id && s.branch_id != this.filters.branch_id)
          return false;
        if (
          this.filters.site_municipality &&
          s.site_municipality != this.filters.site_municipality
        )
          return false;
        if (this.filters.status && s.status != this.filters.status) return false;
        return true;
      });
    },
    // Flatten customer name/contact onto the row so the data-table's default
    // search can match them (the nested `customer` object otherwise stringifies
    // to "[object Object]" and never matches a typed customer name).
    mappedSystems() {
      return this.filteredSystems.map((s) => ({
        ...s,
        customer_name: (s.customer && s.customer.customer_name) || "",
        customer_contact: (s.customer && s.customer.customer_contact_no) || "",
        category_label: this.categoryLabel(s.category),
      }));
    },
    dueMunicipalityItems() {
      const set = new Set(
        this.due_visits
          .map((v) => v.installed_system && v.installed_system.site_municipality)
          .filter(Boolean)
      );
      return Array.from(set).sort();
    },
    dueGroups() {
      let visits = this.due_visits;
      if (this.dueMunicipality) {
        visits = visits.filter(
          (v) =>
            v.installed_system &&
            v.installed_system.site_municipality === this.dueMunicipality
        );
      }
      const map = {};
      visits.forEach((v) => {
        const m =
          (v.installed_system && v.installed_system.site_municipality) ||
          "Unspecified";
        if (!map[m]) map[m] = [];
        map[m].push(v);
      });
      return Object.keys(map)
        .sort()
        .map((municipality) => ({ municipality, visits: map[municipality] }));
    },
  },
  created() {
    this.refresh();
    this.refreshDue();
    if (!this.branches.length) this.get_branches();
  },
  methods: {
    ...mapActions({
      fetch_systems: "installed_system/fetch_systems",
      delete_system: "installed_system/delete_system",
      fetch_due_visits: "installed_system/fetch_due_visits",
      get_branches: "branch/get_branches",
    }),
    refresh() {
      this.fetch_systems();
    },
    refreshDue() {
      this.fetch_due_visits({ days: this.dueDays });
    },
    pendingByCategory(cat) {
      return this.pendingSystems.filter((s) => (s.category || "installed_system") === cat);
    },
    categoryLabel(value) {
      const c = this.categories.find((x) => x.value === value);
      return c ? c.label.replace(/s$/, "") : "Installed System";
    },
    categoryColor(value) {
      return (
        {
          installed_system: "teal",
          device_purchase: "indigo",
          repair: "deep-orange",
        }[value] || "teal"
      );
    },
    isGovernment(item) {
      return !!(item && item.customer && item.customer.is_government);
    },
    dateOnly(d) {
      return d ? String(d).substr(0, 10) : "";
    },
    contactOf(visit) {
      return (
        (visit.installed_system &&
          visit.installed_system.customer &&
          visit.installed_system.customer.customer_contact_no) ||
        ""
      );
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(String(text));
        this.$store.dispatch("notification/set_notification", {
          message: "Contact number copied",
          type: "success",
        });
      } catch (e) {
        this.$store.dispatch("notification/set_notification", {
          message: "Copy failed — " + String(text),
          type: "error",
        });
      }
    },
    statusColor(s) {
      return { active: "green", dormant: "orange", ended: "grey" }[s] || "grey";
    },
    openCreate() {
      this.editing = null;
      this.formDialog = true;
    },
    openEdit(item) {
      this.editing = item;
      this.formDialog = true;
    },
    openDetail(item) {
      this.detailId = item.id;
      this.detailDialog = true;
    },
    openDetailById(id) {
      this.detailId = id;
      this.detailDialog = true;
    },
    openSetup(item) {
      this.setupSystem = item;
      this.setupDialog = true;
    },
    onSaved() {
      this.refresh();
      this.refreshDue();
    },
    onChanged() {
      this.refresh();
      this.refreshDue();
    },
    onSetupSaved() {
      this.refresh();
      this.refreshDue();
    },
    confirmDelete(item) {
      this.$confirm({
        title: "Delete Record",
        message:
          "Delete this aftercare record and its components + maintenance schedule? This cannot be undone.",
        color: "error",
        icon: "mdi-delete",
      }).then(async (ok) => {
        if (!ok) return;
        try {
          await this.delete_system(item.id);
          this.refreshDue();
        } catch (e) {
          this.$alert({
            title: "Error",
            message: "Failed to delete record.",
            color: "error",
            icon: "mdi-alert",
          });
        }
      });
    },
  },
};
</script>
